import { Box, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { Button, Space } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import { StaffRoutes } from "appRoutePaths";
import { useIdentity } from "context/auth";
import { useAuthenticatedRequest } from "hooks/useRequests";
import { InitialPageRequest, PagedResultWithExtra, PageRequest } from "models/common";
import { CurrentBatchExtra, ShipmentBatch, ShipmentBatchType, ShipmentBatchTypeNames, ShipmentCardStatus } from "models/shipping";
import { useCallback, useState } from "react";
import { localizedDate, pageRequestToUrlSearchParams } from "util/helpers";
import { StatusIcon } from "./ShipmentBatchCards";

const CurrentRow = { opacity: 1, backgroundColor: 'inherit' };
const QueuedRow = { opacity: 0.7, backgroundColor: 'var(--dcs-light-gray)' };

const useAssignedBatches = (pageRequest: PageRequest) => {
    const { id } = useIdentity();
    const options = pageRequestToUrlSearchParams(pageRequest);

    const { data, isLoading } = useQuery(
        ["shipment-batches", "to-be-pulled", id],
        useAuthenticatedRequest<PagedResultWithExtra<ShipmentBatch, CurrentBatchExtra>>({
            method: "get",
            url: `/shipping/batches/to-be-pulled?${options}`
        }),
        { enabled: !!id }
    );

    return { data, isLoading };
}

const useShipmentBatchColumns = (extra?: CurrentBatchExtra) => {
    const styledRow = useCallback((batchId: string, value: any) => ({
        props: {
            style: extra?.currentBatchId === batchId
                ? CurrentRow
                : QueuedRow,
        },
        children: <>{value}</>
    }), [extra]);

    const columns: ColumnsType<ShipmentBatch> = [
        {
            title: "Name",
            dataIndex: "name",
            render: (value, rec) => styledRow(rec.id, value)
        },
        {
            title: "Generated",
            dataIndex: "generated",
            render: (d, rec) => styledRow(rec.id, localizedDate(d))
        },
        {
            title: "Type",
            dataIndex: "type",
            render: (t: ShipmentBatchType, rec) => styledRow(rec.id, ShipmentBatchTypeNames[t])
        },
        {
            title: "Orders",
            dataIndex: "orders",
            render: (v, rec) => styledRow(rec.id, v)
        },
        {
            title: "Items",
            dataIndex: "items",
            render: (value, rec) => styledRow(rec.id, (<Space>
                {value}
                {rec.hasShipmentsWithMissingCards && <Button
                    type="link"
                    icon={<StatusIcon status={ShipmentCardStatus.NotPulled} />}
                    href={StaffRoutes.shipmentBatchMissingCards.url(rec.id)}
                />}
            </Space>))
        },
        {
            title: "Pulled",
            dataIndex: "pulled",
            render: (_, rec) => styledRow(rec.id, extra?.currentBatchId === rec.id
                ? <Button type="link" href={StaffRoutes.shipmentBatchCards.url(rec.id)}>
                    {((rec.pulled / rec.items) * 100).toFixed(2)}%
                </Button>
                : `${((rec.pulled / rec.items) * 100).toFixed(2)}%`)
        }
    ];

    return columns;
};

const ShipmentBatchesToPull = () => {
    const [pageRequest, setPageRequest] = useState<PageRequest>(InitialPageRequest);
    const { data, isLoading } = useAssignedBatches(pageRequest);
    const { data: records, totalRecords, pageSize, extra } = data ?? {};
    const columns = useShipmentBatchColumns(extra);

    const tableChanged = (
        // pagination
        { current: newPage }: any,
        // filtering
        _: any,
        // sorting
        __: any
    ) => {
        setPageRequest({ ...pageRequest, page: newPage });
    };

    return <Box p={50}>
        <VStack>
            <Box justifyContent='space-between' display='flex' style={{ width: '100%' }}>
                <Space direction="vertical">
                    <Box fontSize={36}>Shipment Batches To Pull</Box>
                    <Button type="link" href={StaffRoutes.dashboard.url}>
                        &#8592;Back to Dashboard
                    </Button>
                </Space>
            </Box>
            <Box w='100%'>
                <Table
                    id="shipment-batches-table"
                    rowKey={r => r.id}
                    columns={columns}
                    dataSource={records}
                    loading={isLoading}
                    onChange={tableChanged}
                    pagination={{
                        total: totalRecords,
                        current: pageRequest.page,
                        pageSize,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        hideOnSinglePage: true
                    }}
                />
            </Box>
        </VStack>
    </Box>;
}

export default ShipmentBatchesToPull;